import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  CheckCircleIcon, 
  DocumentTextIcon, 
  CalculatorIcon, 
  ScaleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ClockIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  XMarkIcon,
  TruckIcon
} from '@heroicons/react/24/outline'
import api from '../../../services/authService'
import { toast, Toaster } from 'react-hot-toast'

// --- Tipos ---
interface Etapa {
  id: number
  nombre: string
  descripcion: string
}

interface DatosCaso {
  mercancia: any
  documentos: string[]
  etapas: Etapa[]
}

// --- Componente Modal de Documento ---
const VisorDocumento = ({ documento, onClose, datosMercancia }: { documento: string, onClose: () => void, datosMercancia: any }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
      <div className="flex justify-between items-center p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <h3 className="font-bold text-lg text-gray-800 dark:text-white flex items-center gap-2">
          <DocumentTextIcon className="h-5 w-5 text-blue-600" />
          {documento}
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-red-500 transition-colors">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>
      <div className="p-8 overflow-y-auto bg-gray-100 dark:bg-gray-900/50 font-mono text-sm leading-relaxed text-gray-700 dark:text-gray-300">
        <div className="bg-white dark:bg-gray-800 p-8 shadow-sm min-h-[400px] border border-gray-200 dark:border-gray-700">
          <div className="text-center mb-8 border-b-2 border-gray-800 pb-4">
            <h2 className="text-2xl font-bold uppercase tracking-wider">{documento}</h2>
            <p className="text-xs text-gray-500 mt-1">DOCUMENTO DIGITALIZADO - COPIA CERTIFICADA</p>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="block text-xs font-bold text-gray-400 uppercase">Exportador / Vendedor</span>
                <p className="font-bold">Global Trade Suppliers Ltd.</p>
                <p>{datosMercancia.pais_vendedor || 'International Zone'}</p>
              </div>
              <div className="text-right">
                <span className="block text-xs font-bold text-gray-400 uppercase">Factura No.</span>
                <p className="font-bold text-red-600">INV-{Math.floor(Math.random() * 100000)}</p>
                <span className="block text-xs font-bold text-gray-400 uppercase mt-2">Fecha</span>
                <p>2024-12-28</p>
              </div>
            </div>

            <div className="mt-8">
               <table className="w-full text-left">
                 <thead>
                   <tr className="border-b border-gray-300">
                     <th className="py-2">Descripción</th>
                     <th className="py-2 text-right">Cant.</th>
                     <th className="py-2 text-right">Precio Unit.</th>
                     <th className="py-2 text-right">Total</th>
                   </tr>
                 </thead>
                 <tbody>
                   <tr>
                     <td className="py-2">{datosMercancia.descripcion}</td>
                     <td className="py-2 text-right">{datosMercancia.cantidad || 1}</td>
                     <td className="py-2 text-right">${datosMercancia.valor_factura} {datosMercancia.moneda}</td>
                     <td className="py-2 text-right font-bold">${datosMercancia.valor_factura} {datosMercancia.moneda}</td>
                   </tr>
                 </tbody>
               </table>
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200 text-xs text-gray-500 text-center">
              Este documento es una representación simulada con fines educativos.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)

// --- Componente Semaforo Fiscal ---
const SemaforoFiscal = ({ onComplete }: { onComplete: (resultado: 'verde' | 'rojo') => void }) => {
  const [estado, setEstado] = useState<'espera' | 'procesando' | 'resultado'>('espera')
  const [resultado, setResultado] = useState<'verde' | 'rojo' | null>(null)

  const activarMecanismo = () => {
    setEstado('procesando')
    // Simular tiempo de proceso del sistema SAAI
    setTimeout(() => {
      // 20% probabilidad de reconocimiento aduanero (rojo)
      const esRojo = Math.random() < 0.2
      const res = esRojo ? 'rojo' : 'verde'
      setResultado(res)
      setEstado('resultado')
      onComplete(res)
    }, 3000)
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-8 bg-gray-900 rounded-xl p-8 border-4 border-gray-700 shadow-2xl max-w-md mx-auto relative overflow-hidden">
      {/* Luces del Semáforo */}
      <div className="bg-black p-4 rounded-3xl shadow-lg border-2 border-gray-700 flex flex-col gap-4 relative z-10">
        {/* Luz Roja */}
        <div className={`w-24 h-24 rounded-full border-4 border-gray-800 transition-all duration-300 ${
          estado === 'procesando' ? 'animate-pulse bg-red-900' : 
          resultado === 'rojo' ? 'bg-red-600 shadow-[0_0_50px_rgba(220,38,38,0.8)] scale-110' : 'bg-red-950 opacity-30'
        }`}></div>
        
        {/* Luz Verde */}
        <div className={`w-24 h-24 rounded-full border-4 border-gray-800 transition-all duration-300 ${
          estado === 'procesando' ? 'animate-pulse bg-green-900' : 
          resultado === 'verde' ? 'bg-green-500 shadow-[0_0_50px_rgba(34,197,94,0.8)] scale-110' : 'bg-green-950 opacity-30'
        }`}></div>
      </div>

      <div className="text-center z-10">
        <h3 className="text-white text-xl font-bold mb-2">Mecanismo de Selección Automatizada</h3>
        {estado === 'espera' && (
          <button 
            onClick={activarMecanismo}
            className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-lg transform active:scale-95 transition-all"
          >
            ACTIVAR MECANISMO
          </button>
        )}
        {estado === 'procesando' && (
          <p className="text-blue-300 animate-pulse">Conectando con SAAI M3...</p>
        )}
        {estado === 'resultado' && (
          <div className="animate-bounce-in">
            <p className={`text-2xl font-black uppercase ${resultado === 'rojo' ? 'text-red-500' : 'text-green-400'}`}>
              {resultado === 'rojo' ? 'RECONOCIMIENTO ADUANERO' : 'DESADUANAMIENTO LIBRE'}
            </p>
            <p className="text-gray-400 text-sm mt-2">
              {resultado === 'rojo' ? 'La mercancía debe someterse a revisión física y documental.' : 'La mercancía puede salir de la aduana inmediatamente.'}
            </p>
          </div>
        )}
      </div>

      {/* Efecto de fondo Scanlines */}
      <div className="absolute inset-0 pointer-events-none opacity-10 bg-[url('https://media.istockphoto.com/id/1138395421/vector/scan-lines-pattern-overlay-vector-background.jpg?s=612x612&w=0&k=20&c=i3XJqfXqF_Jg_X_X_X_X_X_X_X_X_X_X_X_X_X_X')] bg-cover mix-blend-overlay"></div>
    </div>
  )
}

const PaginaSimulacionCasoA: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [cargando, setCargando] = useState(true)
  const [pasoActual, setPasoActual] = useState(1)
  const [datosCaso, setDatosCaso] = useState<DatosCaso | null>(null)
  const [respuestas, setRespuestas] = useState<any>({})
  const [idIntento, setIdIntento] = useState<number | null>(null)
  
  // Estados para realismo
  const [tiempoRestante, setTiempoRestante] = useState(3600) // 1 hora en segundos
  const [docVisualizado, setDocVisualizado] = useState<string | null>(null)
  const [resultadoSemaforo, setResultadoSemaforo] = useState<'verde' | 'rojo' | null>(null)

  // Timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTiempoRestante(prev => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // Formato de tiempo
  const formatoTiempo = (segundos: number) => {
    const min = Math.floor(segundos / 60)
    const sec = segundos % 60
    return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    const inicializar = async () => {
      try {
        const resCaso = await api.get(`/api/simulaciones/${id}`)
        setDatosCaso(resCaso.data.datos_caso)
        const resIntento = await api.post(`/api/simulaciones/${id}/iniciar`)
        setIdIntento(resIntento.data.id_intento)
        
        if (resIntento.data.datos_progreso) {
           const { etapa_actual, respuestas } = resIntento.data.datos_progreso
           if (etapa_actual) setPasoActual(etapa_actual)
           if (respuestas) setRespuestas(respuestas)
        }
      } catch (error) {
        toast.error('Error al cargar la simulación')
        console.error(error)
      } finally {
        setCargando(false)
      }
    }
    inicializar()
  }, [id])

  const guardarProgreso = async () => {
    if (!idIntento) return
    try {
      await api.put(`/api/simulaciones/intentos/${idIntento}/progreso`, {
        progreso: (pasoActual / (datosCaso?.etapas?.length || 1)) * 100,
        datos_progreso: { etapa_actual: pasoActual, respuestas },
        completado: pasoActual === datosCaso?.etapas?.length
      })
      toast.success('Sistema: Progreso registrado correctamente', { icon: '💾' })
    } catch (error) {
      console.error(error)
    }
  }

  const siguientePaso = () => {
    if (!datosCaso?.etapas) return;
    
    // Validaciones por paso para realismo
    if (pasoActual === 1 && !respuestas.documentosRevisados) {
      toast.error('Debe revisar y marcar todos los documentos antes de continuar.')
      return
    }
    
    guardarProgreso()
    if (pasoActual < datosCaso.etapas.length) {
      setPasoActual(prev => prev + 1)
    } else {
      toast.success('¡Operación Aduanera Concluida!')
      navigate('/institucion/usuarios')
    }
  }

  const anteriorPaso = () => {
    if (pasoActual > 1) setPasoActual(prev => prev - 1)
  }

  if (cargando) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-300">Inicializando Sistema Aduanero...</p>
      </div>
    </div>
  )

  if (!datosCaso || !datosCaso.etapas) return (
    <div className="p-10 text-center">
      <div className="bg-red-50 p-6 rounded-lg text-red-800 border border-red-200">
        Error 500: Datos del caso corruptos o incompletos. Contacte al administrador.
      </div>
    </div>
  )

  const RenderEtapa = () => {
    switch (pasoActual) {
      case 1: // Revisión Documental
        return (
          <div className="space-y-8 animate-fadeIn">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border-l-4 border-blue-500 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-4">
                <DocumentTextIcon className="h-6 w-6 text-blue-500" />
                Ventanilla Digital: Revisión de Documentos
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Revise cuidadosamente cada documento digitalizado. Verifique discrepancias entre la factura y la orden de compra simulada.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Panel de Documentos */}
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {datosCaso.documentos.map((doc, idx) => (
                    <button
                      key={idx}
                      onClick={() => setDocVisualizado(doc)}
                      className="group flex flex-col items-center p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                    >
                      <DocumentTextIcon className="h-12 w-12 text-gray-400 group-hover:text-blue-500 mb-3" />
                      <span className="font-medium text-sm text-center text-gray-700 dark:text-gray-200">{doc}</span>
                      <span className="text-xs text-blue-600 dark:text-blue-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Clic para visualizar</span>
                    </button>
                  ))}
                </div>

                {/* Panel de Checklist */}
                <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    Lista de Verificación
                  </h4>
                  <div className="space-y-3">
                    {['Factura Comercial legible', 'Coincidencia de incoterms', 'País de origen verificado', 'Cantidades correctas'].map((item, i) => (
                      <label key={i} className="flex items-start gap-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer transition-colors">
                        <input type="checkbox" className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{item}</span>
                      </label>
                    ))}
                    <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-600">
                      <label className="flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          checked={respuestas.documentosRevisados || false}
                          onChange={(e) => setRespuestas({...respuestas, documentosRevisados: e.target.checked})}
                          className="h-5 w-5 text-green-600 rounded"
                        />
                        <span className="text-sm font-bold text-gray-800 dark:text-white">Confirmo que he revisado todos los documentos</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Detalles de la Mercancía (Estilo Manifiesto) */}
            <div className="bg-slate-800 text-white p-6 rounded-xl font-mono text-sm shadow-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <span className="block text-slate-400 text-xs uppercase mb-1">Mercancía</span>
                  <div className="font-bold text-lg text-emerald-400">{datosCaso.mercancia.descripcion}</div>
                </div>
                <div>
                  <span className="block text-slate-400 text-xs uppercase mb-1">Origen / Vendedor</span>
                  <div>{datosCaso.mercancia.pais_origen}</div>
                </div>
                <div>
                  <span className="block text-slate-400 text-xs uppercase mb-1">Valor Aduana</span>
                  <div>${datosCaso.mercancia.valor_factura} {datosCaso.mercancia.moneda}</div>
                </div>
                <div>
                  <span className="block text-slate-400 text-xs uppercase mb-1">Peso Bruto</span>
                  <div>{datosCaso.mercancia.peso_bruto || 1500} KG</div>
                </div>
              </div>
            </div>
          </div>
        )
      case 2: // Clasificación
        return (
          <div className="space-y-6 animate-fadeIn">
             <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-6">
                <ScaleIcon className="h-6 w-6 text-purple-600" />
                Sistema de Clasificación Arancelaria (LIGIE)
              </h3>
              
              <div className="flex gap-4 items-start">
                <div className="flex-1 space-y-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <strong>Instrucción:</strong> Identifique la fracción arancelaria correcta para: <br/>
                      <span className="text-lg font-bold block mt-1">{datosCaso.mercancia.descripcion}</span>
                    </p>
                  </div>
                  
                  <div className="relative">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Búsqueda en Tarifa (Simulada)</label>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        placeholder="Ingrese código de 8 dígitos (ej: 8528.72.06)"
                        className="flex-1 p-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-lg tracking-widest"
                        onChange={(e) => setRespuestas({...respuestas, fraccion: e.target.value})}
                        value={respuestas.fraccion || ''}
                      />
                      <button className="px-6 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700">
                        CONSULTAR
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Ingrese la fracción sin puntos para validación rápida.</p>
                  </div>
                </div>
                
                {/* Ayuda Lateral */}
                <div className="w-1/3 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                  <h4 className="font-bold text-sm text-gray-700 dark:text-gray-300 mb-2">Notas Explicativas</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Considere las Reglas Generales 1 y 6. Verifique si la mercancía requiere cumplimiento de RRNA (Regulaciones y Restricciones No Arancelarias).
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      case 3: // Cálculo
        return (
          <div className="space-y-6 animate-fadeIn">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-6">
                <CalculatorIcon className="h-6 w-6 text-emerald-600" />
                Determinación de Contribuciones
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Formulario estilo Pedimento */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 dark:bg-gray-900/50 p-4 rounded border border-gray-200 dark:border-gray-700">
                    <div>
                      <label className="text-xs text-gray-500 uppercase font-bold">Valor Aduana (MXN)</label>
                      <div className="text-xl font-mono font-bold text-gray-800 dark:text-white">$1,000,000.00</div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase font-bold">Tipo de Cambio</label>
                      <div className="text-xl font-mono font-bold text-gray-800 dark:text-white">20.0000</div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 border-b pb-2">Cuadro de Liquidación</h4>
                    
                    {[
                      { label: 'DTA (Derecho Trámite Aduanero)', field: 'dta', hint: 'Art. 49 LFD' },
                      { label: 'Prevalidación (PRV)', field: 'prv', hint: 'Art. 16-A LA' },
                      { label: 'IGI (Impuesto General Importación)', field: 'igi', hint: 'Advalorem' },
                      { label: 'IVA (Impuesto Valor Agregado)', field: 'iva', hint: '16% Base Gravable' }
                    ].map((item) => (
                      <div key={item.field} className="grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-5">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.label}</label>
                          <span className="block text-xs text-gray-400">{item.hint}</span>
                        </div>
                        <div className="col-span-7 relative">
                          <span className="absolute left-3 top-2.5 text-gray-500 font-mono">$</span>
                          <input 
                            type="number" 
                            className="w-full pl-8 py-2 border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500 font-mono text-right dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="0.00"
                            value={respuestas[item.field] || ''}
                            onChange={(e) => setRespuestas({...respuestas, [item.field]: e.target.value})}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Resumen Lateral */}
                <div className="bg-emerald-50 dark:bg-emerald-900/20 p-6 rounded-xl border border-emerald-100 dark:border-emerald-800 h-fit">
                  <h4 className="text-lg font-bold text-emerald-800 dark:text-emerald-300 mb-4">Total a Pagar</h4>
                  <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400 font-mono mb-2">
                    ${(
                      (Number(respuestas.dta) || 0) + 
                      (Number(respuestas.prv) || 0) + 
                      (Number(respuestas.igi) || 0) + 
                      (Number(respuestas.iva) || 0)
                    ).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">
                    *Monto estimado sujeto a validación del prevalidador.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      case 4: // Validación y Semáforo
        return (
          <div className="space-y-6 animate-fadeIn">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-6">
              <TruckIcon className="h-6 w-6 text-indigo-600" />
              Despacho y Selección Automatizada
            </h3>

            {!respuestas.validacion_exitosa ? (
              <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 text-center px-4">
                <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mb-4" />
                <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Validación Requerida</h4>
                <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md">
                  Antes de activar el Mecanismo de Selección Automatizada, debe enviar el pedimento para su validación electrónica y pago.
                </p>
                <button
                  onClick={() => {
                    const loadingToast = toast.loading('Conectando con banco y prevalidador...');
                    setTimeout(() => {
                      toast.dismiss(loadingToast);
                      // Validación simple
                      if (respuestas.fraccion && respuestas.igi && respuestas.iva) {
                        setRespuestas({...respuestas, validacion_exitosa: true, firma_validacion: 'M1803001-' + Date.now()});
                        toast.success('¡Pedimento pagado y validado!');
                      } else {
                        toast.error('Error: Faltan datos críticos en el pedimento.');
                      }
                    }, 2500);
                  }}
                  className="px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-xl transform transition hover:scale-105 flex items-center gap-3"
                >
                  <CurrencyDollarIcon className="h-6 w-6" />
                  PAGAR Y VALIDAR PEDIMENTO
                </button>
              </div>
            ) : !resultadoSemaforo ? (
              // Componente Semáforo
              <SemaforoFiscal onComplete={(res) => setResultadoSemaforo(res)} />
            ) : (
              // Resultado Final
              <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 text-center animate-fadeIn">
                 <div className={`inline-flex p-4 rounded-full mb-6 ${resultadoSemaforo === 'verde' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                   {resultadoSemaforo === 'verde' ? <CheckCircleIcon className="h-16 w-16" /> : <EyeIcon className="h-16 w-16" />}
                 </div>
                 
                 <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-4">
                   {resultadoSemaforo === 'verde' ? 'DESADUANAMIENTO LIBRE' : 'RECONOCIMIENTO ADUANERO'}
                 </h2>
                 
                 <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                   {resultadoSemaforo === 'verde' 
                     ? 'La mercancía ha cumplido con todas las formalidades y puede retirarse del recinto fiscal.' 
                     : 'La autoridad aduanera procederá a la revisión física y documental de la mercancía.'}
                 </p>

                 <div className="bg-gray-100 dark:bg-gray-700 p-6 rounded-lg max-w-lg mx-auto text-left font-mono text-sm mb-8">
                   <p><span className="font-bold">Pedimento:</span> 3548902</p>
                   <p><span className="font-bold">Firma Banco:</span> {respuestas.firma_validacion}</p>
                   <p><span className="font-bold">Hora:</span> {new Date().toLocaleTimeString()}</p>
                   <p><span className="font-bold">Resultado:</span> {resultadoSemaforo.toUpperCase()}</p>
                 </div>
              </div>
            )}
          </div>
        )
      default:
        return <div>Etapa desconocida</div>
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      <Toaster position="top-center" reverseOrder={false} />
      
      {/* Visor de Documentos */}
      {docVisualizado && datosCaso && (
        <VisorDocumento 
          documento={docVisualizado} 
          onClose={() => setDocVisualizado(null)} 
          datosMercancia={datosCaso.mercancia}
        />
      )}

      {/* Header Corporativo / Sistema */}
      <header className="bg-slate-900 text-white shadow-lg sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-400" />
            <div>
              <h1 className="text-lg font-bold leading-none">SISTEMA INTEGRAL DE ADUANAS</h1>
              <span className="text-xs text-slate-400">Práctica Académica v2.0</span>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="hidden md:flex flex-col items-end">
              <span className="text-xs text-slate-400">Tipo de Cambio</span>
              <span className="font-mono font-bold text-green-400">$20.00 MXN</span>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1 rounded bg-slate-800 border ${tiempoRestante < 300 ? 'border-red-500 text-red-400 animate-pulse' : 'border-slate-700 text-blue-300'}`}>
              <ClockIcon className="h-5 w-5" />
              <span className="font-mono font-bold text-lg">{formatoTiempo(tiempoRestante)}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Barra de Progreso */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 mb-8 sticky top-16 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Progreso del Despacho
            </span>
            <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
              Etapa {pasoActual} de {datosCaso?.etapas?.length || 4}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-500 ease-out" 
              style={{ width: `${(pasoActual / (datosCaso?.etapas?.length || 4)) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4">
        {RenderEtapa()}

        {/* Botones de Navegación Flotantes o Fijos */}
        <div className="flex justify-between mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={anteriorPaso}
            disabled={pasoActual === 1}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              pasoActual === 1 
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Anterior
          </button>

          {/* Ocultar botón siguiente si estamos en el resultado final para forzar finalización específica */}
          {!(pasoActual === 4 && resultadoSemaforo) && (
            <button
              onClick={siguientePaso}
              disabled={pasoActual === 4 && !resultadoSemaforo} // En paso 4, requiere semáforo
              className="flex items-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold shadow-md hover:shadow-lg transform active:scale-95 transition-all"
            >
              {pasoActual === (datosCaso?.etapas?.length || 4) ? 'Finalizar Despacho' : 'Siguiente Etapa'}
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          )}
          
          {pasoActual === 4 && resultadoSemaforo && (
             <button
             onClick={siguientePaso}
             className="flex items-center gap-2 px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold shadow-md hover:shadow-lg transform active:scale-95 transition-all animate-bounce"
           >
             Concluir Práctica
             <CheckCircleIcon className="h-5 w-5" />
           </button>
          )}
        </div>
      </main>
    </div>
  )
}

export default PaginaSimulacionCasoA
